/**
 * Northern Shopper — scanned product popup
 * NorthernShopper.ScanModal.showLoading() → spinner up front, returns a token
 * NorthernShopper.ScanModal.open(handle, variantId, token)
 *
 * Types: .dev/types/ns-custom.scan-modal.d.ts
 * Docs: .dev/docs/09-search-and-product-filtering.md (R9.6)
 */
(function () {
	"use strict";

	var NS = (window.NorthernShopper = window.NorthernShopper || {});
	var MODAL_ID = "ns-scan-modal";
	var SECTION_ID = "ns-custom-scan-result";
	// Bumped on every open/close so a slow fetch can never paint over newer state.
	var requestToken = 0;

	function getModal() {
		return document.getElementById(MODAL_ID);
	}

	function getBody(modal) {
		return modal.querySelector("[data-ns-scan-body]");
	}

	function getRootUrl() {
		var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
		return root.endsWith("/") ? root : root + "/";
	}

	function setLoading(modal, loading) {
		var spinner = modal.querySelector("[data-ns-scan-loading]");
		if (spinner) spinner.hidden = !loading;
	}

	function isOpen() {
		var modal = getModal();
		return !!modal && !modal.hidden;
	}

	function close() {
		var modal = getModal();
		if (!modal) return;
		requestToken++;
		if (modal.contains(document.activeElement) && document.activeElement.blur) {
			document.activeElement.blur();
		}
		modal.hidden = true;
		modal.setAttribute("aria-hidden", "true");
		document.body.classList.remove("ns-custom-modal-open");
		setLoading(modal, false);
		var body = getBody(modal);
		if (body) body.innerHTML = "";
	}

	function show(modal) {
		modal.hidden = false;
		modal.setAttribute("aria-hidden", "false");
		document.body.classList.add("ns-custom-modal-open");
	}

	/** Mirrors the community modal's landed/regular choice onto the card's price label. */
	function syncPriceMode(scope) {
		var mode = NS.showLanded === false ? "regular" : "landed";
		(scope || document).querySelectorAll("[data-ns-scan-price-mode]").forEach(function (el) {
			el.hidden = el.getAttribute("data-ns-scan-price-mode") !== mode;
		});
	}

	function render(modal, html) {
		var body = getBody(modal);
		var card = new DOMParser().parseFromString(html, "text/html").querySelector("[data-ns-scan-card]");
		if (!body || !card || card.hasAttribute("data-ns-scan-empty")) return false;

		body.innerHTML = "";
		body.appendChild(card);
		syncPriceMode(body);

		// The card carries a data-ns-price payload and a data-ns-card-qty control,
		// so hand it to the pricing engine and the shared cart-qty sync.
		if (NS.Pricing && typeof NS.Pricing.recalculateAll === "function") {
			NS.Pricing.recalculateAll();
		}
		if (NS.CardQty && typeof NS.CardQty.updateAllCards === "function") {
			NS.CardQty.updateAllCards();
		}
		return true;
	}

	/** Empty popup + spinner, focused on the close button. */
	function paintLoading(modal) {
		var body = getBody(modal);
		if (body) body.innerHTML = "";
		setLoading(modal, true);
		show(modal);

		// Focused here rather than on the qty input so a follow-up scan is not
		// swallowed by the input (the scanner ignores editable targets).
		var closeBtn = modal.querySelector("[data-ns-scan-close]");
		if (closeBtn) closeBtn.focus();
	}

	/**
	 * Show the popup in its loading state before the scanned code has resolved,
	 * so a scan feels immediate instead of waiting on the lookup round-trips.
	 *
	 * Returns a token to pass to `open`. Dismissing the popup while the lookup is
	 * still running invalidates the token, which cancels the pending render.
	 */
	function showLoading() {
		var modal = getModal();
		if (!modal) return null;
		var token = ++requestToken;
		paintLoading(modal);
		return token;
	}

	/**
	 * Show the scanned product.
	 *
	 * Resolves `true` when rendered, `false` when it could not be (unknown
	 * handle or network error), and `null` when the request was dismissed or
	 * superseded — callers should treat `null` as "no longer wanted" rather than
	 * as a failure worth reporting. All three keep the usual truthiness check.
	 *
	 * Rendered on the product route rather than `/variants/{id}`: that route only
	 * 302s to the product page when `section_id` is absent, and the product route
	 * gives the section a normal `product` context.
	 *
	 * Pass the `showLoading` token to continue that popup rather than restart it.
	 */
	function open(handle, variantId, loadingToken) {
		var modal = getModal();
		if (!modal || !handle) return Promise.resolve(false);
		if (loadingToken != null && loadingToken !== requestToken) return Promise.resolve(null);

		var token = loadingToken != null ? loadingToken : ++requestToken;
		var url = getRootUrl() + "products/" + encodeURIComponent(handle) + "?section_id=" + encodeURIComponent(SECTION_ID);
		if (variantId) url += "&variant=" + encodeURIComponent(variantId);

		paintLoading(modal);

		return fetch(url)
			.then(function (r) {
				if (!r.ok) throw new Error("scan section fetch failed");
				return r.text();
			})
			.then(function (html) {
				if (token !== requestToken) return null;
				setLoading(modal, false);
				var rendered = render(modal, html);
				if (!rendered) close();
				return rendered;
			})
			.catch(function () {
				if (token !== requestToken) return null;
				close();
				return false;
			});
	}

	function bindEvents() {
		var modal = getModal();
		if (!modal) return;

		modal.querySelectorAll("[data-ns-scan-close]").forEach(function (btn) {
			btn.addEventListener("click", close);
		});
		modal.addEventListener("click", function (e) {
			if (e.target === modal) close();
		});
		document.addEventListener("keydown", function (e) {
			if (e.key === "Escape" && isOpen()) close();
		});
		document.addEventListener("ns:landed:changed", function () {
			syncPriceMode(getBody(modal));
		});
	}

	NS.ScanModal = {
		showLoading: showLoading,
		open: open,
		close: close,
		isOpen: isOpen,
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", bindEvents);
	} else {
		bindEvents();
	}
})();
